<script setup lang="ts">
import axios from 'axios'
import AuthContainer from './components/AuthContainer.vue'
import AuthForm from './components/AuthForm.vue'
import type { AuthFormData } from '../../types/types'

const notification = useNotification()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const user = ref({
  email: '',
  password: '',
  confirmPassword: ''
})

const code = ref('')
const isLoaded = ref(true)

user.value.email = route.query.email.toString()
code.value = route.query.code.toString()

const checkToken = async () => {
  try {
    await axios.get('/auth/password-token', {
      params: {
        email: user.value.email,
        code: code.value
      }
    })
  } catch {
    notification.create('error', {
      text: t('login.reset.error.expiration'),
      persist: true
    })
    router.push({ name: 'ForgotPassword' })
  }
}

checkToken()

const handleSubmit = async (data: AuthFormData) => {
  if (!isLoaded.value) {
    return
  }
  isLoaded.value = false
  try {
    await axios.post('/auth/password', {
      email: data.email,
      password: data.password,
      token: code.value
    })
    notification.create('success', {
      text: t('login.reset.success'),
      persist: true
    })
    router.push({ name: 'Login' })
  } catch (error) {
    if (error.response.status === 422) {
      if (error.response.data.errors.password) {
        notification.create('error', {
          text: t('login.reset.error.password_length'),
          persist: true
        })
        return
      }
    }
    if (error.response.status === 403) {
      notification.create('error', {
        text: t('login.reset.error.expiration'),
        persist: true
      })
    }
  } finally {
    isLoaded.value = true
  }
}
</script>

<template>
  <AuthContainer :title="t('login.reset.title')">
    <AuthForm :is-loaded="isLoaded" page-type="ResetPassword" @on-submit="handleSubmit" />
  </AuthContainer>
</template>
